// ── DB 관리 — 스키마 뷰 컴포넌트 ─────────────────────────────────────────────
// 테이블의 컬럼·인덱스·외래키 정보를 표로 표시
import React from 'react';
import {
  sectionTitleBarStyle, tableWrapStyle, tblStyle, thStyle, trStyle, tdStyle,
  ordinalStyle, colNameStyle, typeBadgeStyle, sensitiveBadgeStyle,
  pkBadgeStyle, uniqueBadgeStyle, mobileColCardStyle,
} from './styles';
import { typeColor } from './utils';

/**
 * 스키마 뷰: 컬럼 목록 + 인덱스 + 외래키 테이블
 * @param {object} schema  — API에서 받은 스키마 데이터 (columns, indexes, foreign_keys)
 * @param {boolean} isMobile — 모바일 여부 (true면 카드, false면 테이블)
 */
export function SchemaView({ schema, isMobile }) {
  return (
    <div style={{ display: 'grid', gap: 20 }}>

      {/* ── 컬럼 섹션 ── */}
      <section>
        <div style={sectionTitleBarStyle}>컬럼 ({schema.columns?.length})</div>

        {isMobile ? (
          /* 모바일: 컬럼 카드 목록 */
          <div style={{ display: 'grid', gap: 6, padding: '0 12px 12px' }}>
            {schema.columns?.map((col, idx) => {
              const tc = typeColor(col.type);
              return (
                <div key={col.name} style={mobileColCardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={ordinalStyle}>{idx + 1}</span>
                    <span style={colNameStyle}>{col.name}</span>
                    <span style={{ ...typeBadgeStyle, background: tc.bg, color: tc.text, fontSize: 10 }}>{col.type}</span>
                    {col.is_sensitive && <span style={sensitiveBadgeStyle}>🔒</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                    {col.max_length != null && <span>길이: {col.max_length}</span>}
                    <span style={{ color: col.nullable ? 'var(--text-muted)' : '#dc2626', fontWeight: 700 }}>
                      NULL: {col.nullable ? 'YES' : 'NO'}
                    </span>
                    {col.default && <span>기본: {col.default}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* 데스크톱: 컬럼 테이블 */
          <div style={tableWrapStyle}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  {['#', '컬럼명', '타입', '길이', 'NULL', '기본값', ''].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schema.columns?.map((col, idx) => {
                  const tc = typeColor(col.type);
                  return (
                    <tr key={col.name} style={trStyle(idx)}>
                      <td style={tdStyle}><span style={ordinalStyle}>{idx + 1}</span></td>
                      <td style={tdStyle}><span style={colNameStyle}>{col.name}</span></td>
                      <td style={tdStyle}>
                        <span style={{ ...typeBadgeStyle, background: tc.bg, color: tc.text }}>{col.type}</span>
                      </td>
                      <td style={tdStyle}>{col.max_length ?? '-'}</td>
                      <td style={tdStyle}>
                        <span style={{ color: col.nullable ? 'var(--text-muted)' : '#dc2626', fontWeight: 700, fontSize: 11 }}>
                          {col.nullable ? 'YES' : 'NO'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 11 }}>
                        {col.default || '-'}
                      </td>
                      <td style={tdStyle}>
                        {col.is_sensitive && (
                          <span style={sensitiveBadgeStyle} title="민감 컬럼 — 데이터 조회 시 마스킹됨">🔒 마스킹</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 인덱스 섹션 ── */}
      {schema.indexes?.length > 0 && (
        <section>
          <div style={sectionTitleBarStyle}>인덱스 ({schema.indexes.length})</div>
          <div style={tableWrapStyle}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  {['인덱스명', '컬럼', 'PK', 'UNIQUE'].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schema.indexes.map((idx, i) => (
                  <tr key={idx.index_name} style={trStyle(i)}>
                    <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{idx.index_name}</span></td>
                    <td style={tdStyle}>{Array.isArray(idx.columns) ? idx.columns.join(', ') : idx.columns}</td>
                    <td style={tdStyle}>{idx.is_primary && <span style={pkBadgeStyle}>PK</span>}</td>
                    <td style={tdStyle}>{idx.is_unique && <span style={uniqueBadgeStyle}>UNIQUE</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── 외래키 섹션 ── */}
      {schema.foreign_keys?.length > 0 && (
        <section>
          <div style={sectionTitleBarStyle}>외래 키 ({schema.foreign_keys.length})</div>
          <div style={tableWrapStyle}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  {['컬럼', '참조 테이블', '참조 컬럼'].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schema.foreign_keys.map((fk, i) => (
                  <tr key={`${fk.column_name}-${fk.foreign_table}`} style={trStyle(i)}>
                    <td style={tdStyle}><span style={colNameStyle}>{fk.column_name}</span></td>
                    <td style={tdStyle}><span style={{ ...colNameStyle, color: '#0369a1' }}>{fk.foreign_table}</span></td>
                    <td style={tdStyle}>{fk.foreign_column}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
